import { TutorialPages } from './model.ts';

export enum OnboardingPages {
  AllowNotifications = 'allow-notifications',
  ChooseUser = 'choose-user',
  OrgChoice = 'org-choice',
  Demo = 'demo',
  Download = 'download',
  HouseholdStart = 'household-start',
  HouseholdLastName = 'household-last-name',
  HouseholdEmail = 'household-email',
  HouseholdFirstName = 'household-first-name',
  HouseholdNotFound = 'household-not-found',
  HouseholdJoin = 'household-join',
  HouseholdCreate = 'household-create',
  HouseholdFind = 'household-find',
  HouseholdWelcome = 'household-welcome',
  SubscribeStart = 'subscribe-start',
  SubscribeFindHousehold = 'subscribe-find-household',
  SubscribePayment = 'subscribe-payment',
  SubscribeThankYou = 'subscribe-thank-you',
  SurveyDiscovery = 'survey-discovery',
  SurveyHouseholdSchedule = 'survey-household-schedule',
  ShareMusicLeader = 'share-music-leader',
}

export enum Pages {
  About = 'about',
  AddMember = 'add-member',
  AdminInvite = 'admin-invite',
  Dashboard = 'dashboard',
  EventIsland = 'event',
  EventCalendar = 'event-calendar',
  Engineering = 'engineering',
  EngineeringAudioLab = 'engineering-audio-lab',
  EngineeringBrokenSongs = 'engineering-broken-songs',
  EngineeringCustomMusic = 'engineering-custom-music',
  EngineeringManageHouseholds = 'engineering-manage-households',
  Favorites = 'favorites',
  Help = 'help',
  Household = 'household',
  HouseholdInvite = 'household-invite',
  // Retired household invite route, kept for good: invite links already sent by email still point
  // here, and links in the outside world never expire (backward-compatibility skill).
  DeprecatedHouseholdInvite = 'church-household-invite',
  Huddle = 'huddle',
  Learn = 'learn',
  Library = 'library',
  Loading = 'loading',
  Onboarding = 'onboarding',
  Planner = 'planner',
  Privacy = 'privacy',
  SeasonalCalendar = 'seasonal-planner',
  Settings = 'settings',
  SubscriptionTrial = 'subscription-trial',
  ThisWeek = 'this-week', // leaving for backwards compatiblity with stored hyperlinks
  Tutorials = 'tutorials',
  Organization = 'organization',
  Today = 'today',
}

// Paths that once named a page and went out into the world (an email, a printed bulletin), so they
// never expire (backward-compatibility skill): the server answers each with a permanent redirect to
// where the page lives now, and the client router does the same for a browser whose service worker
// serves the app for every navigation. Not in `Pages`, so nothing treats them as a current page.
export const retiredPagePaths: Readonly<Record<string, Pages>> = {
  // The Planner's path until #1373; the Sunday Music Request email linked here until #4009.
  'liturgy-planner': Pages.Planner,
};

// Learn tab sub-paths under /learn (issues #3470, #3719): a class screen sits one level down and
// the modules it lists one level below it (/learn/pitch, /learn/pitch/match); the Range Finder,
// which belongs to no class, sits on its own. Not persisted anywhere, unlike TutorialPages.
export enum LearnPages {
  Range = 'range',
  Pitch = 'pitch', // the Pitch class screen
  PitchMatch = 'pitch/match',
  IntervalTraining = 'pitch/intervals',
  SingInSolfege = 'pitch/solfege',
  Rhythm = 'rhythm', // the Rhythm class screen
  RhythmEcho = 'rhythm/echo',
  SingRhythm = 'rhythm/sing',
}

export enum Islands {
  Subscribe = 'subscribe',
  Download = 'download',
  Demo = 'demo',
}

export function getOnboardingPath(page?: OnboardingPages) {
  return !page ? `/${Pages.Onboarding}` : `/${Pages.Onboarding}#${page}`;
}

// Query parameters the server and client share on onboarding URLs (the client keeps its
// own list of client-only ones in client/src/pages/onboarding/common.tsx).
// `subscribe` marks the subscribe context; `promo` names a promotional campaign slug that the
// server maps to a Stripe promotion code (GitHub issue #3551).
export const SubscribeContextQueryString = 'subscribe';
export const PromoCampaignQueryString = 'promo';

// Landing URL for a promotional campaign: the subscribe flow with the campaign slug attached.
// Both parameters ride through every onboarding page and reach the checkout request.
export function getPromoOnboardingPath(promoSlug: string) {
  const params = new URLSearchParams({ [SubscribeContextQueryString]: '', [PromoCampaignQueryString]: promoSlug });
  return `/${Pages.Onboarding}?${params.toString()}#${OnboardingPages.SubscribeStart}`;
}

export function getTutorialPath(page?: TutorialPages) {
  return !page ? `/${Pages.Tutorials}` : `/${Pages.Tutorials}/${page}`;
}

export function getLearnPath(page?: LearnPages) {
  return !page ? `/${Pages.Learn}` : `/${Pages.Learn}/${page}`;
}

// The Learn sub-page a pathname names (getLearnPath's inverse), or undefined for the hub itself and
// for anything under /learn that is not one of LearnPages. A trailing slash names the same page.
export function learnPageFromPath(pathname: string): LearnPages | undefined {
  const prefix = `/${Pages.Learn}/`;
  if (!pathname.startsWith(prefix)) {
    return;
  }
  const subPath = pathname.slice(prefix.length).replace(/\/+$/, '');
  return Object.values(LearnPages).find((page) => page === subPath);
}
